import React, { AnimationEvent, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActionTextStringAssets, ControlsStringAssets, MessagesStringAssets } from '../../../assets/stringAssets';
import { ILoginData } from '../../../dataObjects/models/registerAndLogin/LoginData';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, BoxProps, Button, ButtonProps, LinearProgress, LinearProgressProps, TextField, Typography, TypographyProps } from '@mui/material';
import SubmitButton from '../../controls/buttons/SubmitButton/SubmitButton';
import Form from '../Form/Form';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/styles';
import TwoButtonAcceptanceDialog from '../../dialogs/TwoButtonAcceptanceDialog/TwoButtonAcceptanceDialog';
import { composeMessageUsingStringAsset } from '../../messages';
import { RegularExpressions } from '../../../assets/regularExpressionAssets';
import OneButtonDialog from '../../dialogs/OneButtonDialog/OneButtonDialog';
import { isEmail } from 'validator';

/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area to display the overall Progress Bar Area
const StyledBoxForProgressBarArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "100%",
}));

// a styled LinearProgress for the Progress Bar
const StyledLinearProgressForProgressBar = styled((props: LinearProgressProps) => (
  <LinearProgress
    {...props}
  />
))(({ theme }) => ({
  width: "100%",
}));

// a styled Box (equivalent to a <div>), providing an area to display the Progress Label Area
const StyledBoxForProgressLabelArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  width: "100%",
}));

// a styled Typography for the Progress Label
const StyledTypographyForProgressLabel = styled((props: TypographyProps) => (
  <Typography
    variant="caption"
    {...props}
  />
))(({ theme }) => ({
}));

// a styled Box (equivalent to a <div>), providing an area to display the Action Buttons Area
const StyledBoxForActionButtonsArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  flexDirection: 'column',
}));

// a styled Box (equivalent to a <div>), providing an area to display a Pseudo Horizontal Rule
const StyledBoxForPseudoHorizontalRule = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  lineHeight: '0.1em',
  margin: '20px 0px',
}));

// a styled Box (equivalent to a <div>), providing an area to display Text within the Pseudo Horizontal Rule
const StyledBoxForTextInPseudoHorizontalRule = styled((props: BoxProps) => (
  <Box
    component='span'
    {...props}
  />
))(({ theme }) => ({
  background: '#fff',
  padding: '0 10px',
}));

// a styled Box as a <span>, providing an area to display action Text (text that is clickable)
const StyledSpanForActionText = styled((props: BoxProps) => (
  <Box
    component='span'
    {...props}
  />
))(({ theme }) => ({
  color: theme.actionText.color,
  cursor: theme.actionText.cursor,
  fontSize: theme.actionText.fontSize,
  textDecorationLine: theme.actionText.textDecorationLine,
  padding: '0 10px',
  '&:hover': {
    color: theme.actionText.hoverColor,
    textDecorationColor: theme.actionText.hoverTextDecorationColor,
  },
}));

// a styled Button for the Register Button
const StyledButtonForRegisterButton = styled((props: ButtonProps) => (
  <Button
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  padding: theme.spacing(1, 2),
  color: theme.secondaryButton.textColor,
  border: theme.secondaryButton.border,
  background: theme.secondaryButton.background,
  '&:hover': {
    border: theme.secondaryButton.hoverBorder,
    background: theme.secondaryButton.hoverBackground,
    color: theme.secondaryButton.hoverTextColor,
  },
  '&:disabled': {
    border: theme.secondaryButton.disabledBorder,
    background: theme.secondaryButton.disabledBackground,
    color: theme.secondaryButton.disabledTextColor,
  },
}));

interface ILoginFormValues {
  email: string;
  password: string;
}

// using 'yup', set up a schema for the form field values
const schema = yup.object().shape({
  email: yup
    .string()
    .email(ControlsStringAssets.emailInvalid)
    .required(ControlsStringAssets.emailRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
  password: yup
    .string()
    .min(8, ControlsStringAssets.passwordMinLength)
    .matches(RegularExpressions.Password, ControlsStringAssets.passwordPattern)
    .required(ControlsStringAssets.passwordRequired)
    .when('autofilled', ([autofilled], schema) => {
      return autofilled ? schema.notRequired() : schema.required('Required');
    }),
});

// This LoginForm detects when the browser has autofilled the Email and Password fields, conceptually based on the information in the following article:
// https://trailheadtechnology.com/autofill-with-react-hook-form-yup-and-mui/
export interface ILoginFormProps extends PropsWithChildren<unknown> {
  /**
   * @property {ILoginData} loginData The LoginData details for the form (in production, the fields will be blank; in development, default credentials may be provided)
   */
  loginData: ILoginData,
  /**
   * @property {boolean} accountNotVerified Whether it has been determined that the account has not been verified
   */
  accountNotVerified: boolean,
  /**
   * @property {string} emailAtLastSubmit The email address provided at the last Login submit
   */
  emailAtLastSubmit?: string,
  /**
   * @property {boolean} loginRequestInProgress Whether a login request is in progress
   */
  loginRequestInProgress?: boolean,
  /**
   * @property {(loginData: ILoginData) => Promise<void>} onSubmit Method to call for submitting the form for a login operation
   */
  onSubmit: (loginData: ILoginData) => Promise<void>,
  /**
   * @property {(emailAddress: string) => Promise<void>} onRequestAccountVerificationEmail Method to call when user requests to re-send
   *                                                     account verification email
   */
  onRequestAccountVerificationEmail: (emailAddress: string) => Promise<void>,
  /**
   * @property {(emailAddress: string) => Promise<void>} onRequestPasswordResetEmail Method to call when user requests to send a
   *                                                     password reset email
   */
  onRequestPasswordResetEmail: (emailAddress: string) => Promise<void>,
  /**
   * @property {(emailAddress: string) => Promise<void>} onRequestSigninWithEmailLinkEmail Method to call when user requests to send a
   *                                                     sign-in with email link email
   */
  onRequestSigninWithEmailLinkEmail: (emailAddress: string) => Promise<void>,
}

const LoginForm: React.FC<ILoginFormProps> = (props: ILoginFormProps) => {
  LoginForm.displayName = 'Login Form';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  displayConsoleLogs && console.log('%c Entered/Refreshed LoginForm component', 'background: #055; color: #fff');

  const { loginData, loginRequestInProgress, accountNotVerified, emailAtLastSubmit, onSubmit,
    onRequestAccountVerificationEmail, onRequestPasswordResetEmail, onRequestSigninWithEmailLinkEmail } = props;

  const navigate = useNavigate();

  // set up details for ReactHookForm
  const { control, formState, formState: { errors, isValid }, getValues, handleSubmit, register, setValue, trigger } = useForm<ILoginFormValues>({
    // const { control, formState, formState: { errors }, getValues, handleSubmit, register, trigger } = useForm<FormFields>({
    defaultValues: {
      email: loginData.email ? loginData.email : '',
      password: loginData.password,
    },
    mode: "all", // trigger validation of the form for all modes
    resolver: yupResolver(schema)
  });

  const { ref: emailReg, ...emailProps } = register("email", { required: true });
  const { ref: passwordReg, ...passwordProps } = register("password", { required: true });

  // capture the current value of relevant form fields
  const [currentEmailAddress, setCurrentEmailAddress] = useState<string>(loginData.email ?? '');
  const [currentPassword, setCurrentPassword] = useState<string>(loginData.password ?? '');

  // whether to show the Re-send Account Verification Email Confirmation dialog
  const [showResendVerificationEmailConfirmationDlg, setShowResendVerificationEmailConfirmationDlg] = useState<boolean>(false);

  // whether to show the Password Reset Confirmation dialog
  const [showSendPasswordResetEmailConfirmationDlg, setShowSendPasswordResetEmailConfirmationDlg] = useState<boolean>(false);

  // whether to show the Send Sign-in With Email Link Confirmation dialog
  const [showSendSigninWithEmailLinkEmailConfirmationDlg, setShowSendSigninWithEmailLinkEmailConfirmationDlg] = useState<boolean>(false);

  // whether to show the "Email Required" dialog
  const [showEmailRequiredDlg, setShowEmailRequiredDlg] = useState<boolean>(false);

  const [isEmailAddressAutoFilled, setIsEmailAddressAutoFilled] = useState<boolean>(false);
  const [isPasswordAutoFilled, setIsPasswordAutoFilled] = useState<boolean>(false);

  const [formIsValid, setFormIsValid] = useState<boolean>(false);

  // determines whether the email address field value is valid
  const validEmailAddressFieldValue: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c LoginForm. In validEmailAddressFieldValue(). currentEmailAddress: ${currentEmailAddress}'`, 'background: #055; color: #fff');
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentEmailAddress !== undefined) {
        isFieldValueValid = isEmail(currentEmailAddress);
        displayConsoleLogs && console.log(`%c LoginForm. In validEmailAddressFieldValue() checking password RegEx. currentEmailAddress: ${currentEmailAddress}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isEmailAddressAutoFilled;
        displayConsoleLogs && console.log(`%c LoginForm. In validEmailAddressFieldValue() checking password AutoFill. currentEmailAddress: ${currentEmailAddress}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      }

      displayConsoleLogs && console.log(`%c LoginForm. In validEmailAddressFieldValue() ready to return. currentEmailAddress: ${currentEmailAddress}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      return isFieldValueValid;
    },
    [currentEmailAddress, isEmailAddressAutoFilled]
  );

  // determines whether the password field value is valid
  const validPasswordFieldValue: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c LoginForm. In validPasswordFieldValue(). currentPassword: ${currentPassword}'`, 'background: #055; color: #fff');
      let isFieldValueValid: boolean = false;

      // see if the explicit value in the field (via a backing state variable) is valid
      if (currentPassword !== undefined) {
        isFieldValueValid = RegularExpressions.Password.test(currentPassword);
        displayConsoleLogs && console.log(`%c LoginForm. In validPasswordFieldValue() checking password RegEx. currentPassword: ${currentPassword}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      }

      // if the explicit value in the field was not found to be valid, see if the value was autofilled (which, by definition,
      // would mean that the value was used before and, thus, had a valid pattern)
      if (!isFieldValueValid) {
        isFieldValueValid = isPasswordAutoFilled;
        displayConsoleLogs && console.log(`%c LoginForm. In validPasswordFieldValue() checking password AutoFill. currentPassword: ${currentPassword}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      }

      displayConsoleLogs && console.log(`%c LoginForm. In validPasswordFieldValue() ready to return. currentPassword: ${currentPassword}; isFieldValueValid: ${isFieldValueValid}'`, 'background: #055; color: #fff');
      return isFieldValueValid;
    },
    [currentPassword, isPasswordAutoFilled]
  );

  const allRequiredFieldValuesValid: () => boolean = useCallback(
    () => {
      displayConsoleLogs && console.log(`%c LoginForm. In allRequiredFieldValuesValid(). validEmailAddressFieldValue(): ${validEmailAddressFieldValue()}; validPasswordFieldValue(): ${validPasswordFieldValue()}'`, 'background: #055; color: #fff');
      return validEmailAddressFieldValue() && validPasswordFieldValue();
    },
    [validEmailAddressFieldValue, validPasswordFieldValue]
  );

  const onEmailAddressAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isEmailAddressAutoFilled) {
            setIsEmailAddressAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart(); getValues('email'): ${getValues('email')}`, 'background: #055; color: #fff');
          displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart(); getValues('password'): ${getValues('password')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  const onPasswordAnimationStart = useCallback(
    () =>
      (event: AnimationEvent<HTMLDivElement>): void => {
        displayConsoleLogs && console.log(`%c LoginForm. In onPasswordAnimationStart()`, 'background: #055; color: #fff');

        if (event.animationName === 'mui-auto-fill') {
          displayConsoleLogs && console.log(`%c LoginForm. In onPasswordAnimationStart(). event.animationName === 'mui-auto-fill'`, 'background: #055; color: #fff');

          if (!isPasswordAutoFilled) {
            setIsPasswordAutoFilled(true);
          }

          displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart(); getValues('email'): ${getValues('email')}`, 'background: #055; color: #fff');
          displayConsoleLogs && console.log(`%c LoginForm. In onEmailAddressAnimationStart(); getValues('password'): ${getValues('password')}`, 'background: #055; color: #fff');
        }
      },
    []
  );

  // useEffect(() => {
  //   setFormIsValid(isValid || (isEmailAddressAutoFilled && isPasswordAutoFilled));
  // }, [isValid, isEmailAddressAutoFilled, isPasswordAutoFilled]);

  useEffect(() => {
    setFormIsValid(isValid || allRequiredFieldValuesValid());
  }, [isValid, allRequiredFieldValuesValid]);

  // whether a request is currently being processed (eg, "Re-send Account Verification Email", "Request Email Sign-in", "Reset Password", etc.)
  const [requestBeingProcessed, setRequestBeingProcessed] = useState<boolean>(false);

  // label for a request currently being processed
  const [requestBeingProcessedLabel, setRequestBeingProcessedLabel] = useState<string>('');

  // state value indicating whether a login is in progress
  const [loginInProgress, setLoginInProgress] = useState<boolean>(loginRequestInProgress ?? false);

  // useEffect hook for setting the 'loginInProgress' local state based on whether a login is currently in progress
  useEffect(() => {
    setLoginInProgress(loginRequestInProgress ?? false);
  }, [loginRequestInProgress]);

  // handles a login (submit) request from the form
  const handleLoginSubmit = async (data: ILoginFormValues) => {

    setLoginInProgress(true);

    // fill in email & password within the loginData object passed in
    loginData.email = data.email;
    loginData.password = data.password;

    // call the onSubmit handler passed in, supplying the Channel object
    await onSubmit(loginData);
  }

  // // An 'onChange' handler for the Email field
  // function handleEmailChange(emailAddressFieldValue: string): void {
  //   displayConsoleLogs && console.log(`%c LoginForm.handleEmailChange. emailAddressFieldValue: ${emailAddressFieldValue}`, 'background: #055; color: #fff');

  //   if (isEmail(emailAddressFieldValue)) {
  //     if (getValues('email') !== emailAddressFieldValue) {
  //       setCurrentEmailAddress(emailAddressFieldValue);
  //       displayConsoleLogs && console.log(`%c LoginForm. getValues('email') has been set.`, 'background: #055; color: #fff');
  //     }
  //   }
  // }

  const handleResendVerificationEmail = async () => {
    setShowResendVerificationEmailConfirmationDlg(true);
  }

  const handleResendVerificationEmailConfirmed = async () => {
    // set flag and label to display a progress indicator
    setRequestBeingProcessed(true);
    setRequestBeingProcessedLabel(MessagesStringAssets.login_RequestBeingProcessed);

    // call method to reset the local state details for hiding/showing the Re-send Verification Email dialog
    resendVerificationEmailConfirmationReset();

    // call the provided method to request creating an email request for re-sending an account verification email
    await onRequestAccountVerificationEmail(getValues('email'));

    resetRequestBeingProcessed();
  }

  function handleResendVerificationEmailCanceled(): void {
    // call method to reset the local state details for hiding/showing the Re-send Verification Email dialog
    resendVerificationEmailConfirmationReset();
  }

  function resendVerificationEmailConfirmationReset() {
    // set flag to hide the delete confirmation dialog
    setShowResendVerificationEmailConfirmationDlg(false);
  }

  const handleSendSigninWithEmailLinkEmail = async () => {
    // ensure that the email address provided is valid and it's not autofilled (because we can't directly access an autofilled value)
    // if (validEmailAddressFieldValue() && !isEmailAddressAutoFilled) {
    if (validEmailAddressFieldValue()) {
      setShowSendSigninWithEmailLinkEmailConfirmationDlg(true);
    } else {
      setShowEmailRequiredDlg(true);
    }
  }

  const handlePasswordResetEmail = async () => {
    // ensure that the email address provided is valid and it's not autofilled (because we can't directly access an autofilled value)
    // if (validEmailAddressFieldValue() && !isEmailAddressAutoFilled) {
    if (validEmailAddressFieldValue()) {
      setShowSendPasswordResetEmailConfirmationDlg(true);
    } else {
      setShowEmailRequiredDlg(true);
    }
  }

  const handlePasswordResetEmailConfirmed = async () => {
    // set flag and label to display a progress indicator
    setRequestBeingProcessed(true);
    setRequestBeingProcessedLabel(MessagesStringAssets.login_RequestBeingProcessed);

    // call method to reset the local state details for hiding/showing the Password Reset Email dialog
    sendPasswordResetEmailConfirmationReset();

    // call the provided method to request creating an email request for sending the Password Reset email
    // await onRequestPasswordResetEmail(getValues('email'));
    await onRequestPasswordResetEmail(currentEmailAddress);

    resetRequestBeingProcessed();
  }

  function handleSendPasswordResetEmailCanceled(): void {
    // call method to reset the local state details for hiding/showing the Password Reset Email dialog
    sendPasswordResetEmailConfirmationReset();
  }

  function sendPasswordResetEmailConfirmationReset() {
    // set flag to hide the delete confirmation dialog
    setShowSendPasswordResetEmailConfirmationDlg(false);
  }

  const handleSendSigninWithEmailLinkEmailConfirmed = async () => {
    // set flag and label to display a progress indicator
    setRequestBeingProcessed(true);
    setRequestBeingProcessedLabel(MessagesStringAssets.login_RequestBeingProcessed);

    // call method to reset the local state details for hiding/showing the Sign-in With Email Link Email dialog
    sendSigninWithEmailLinkEmailConfirmationReset();

    // call the provided method to request creating an email request for sending the Sign-in With Email Link email
    // await onRequestSigninWithEmailLinkEmail(getValues('email'));
    // await onRequestSigninWithEmailLinkEmail(getValues('email'));
    await onRequestSigninWithEmailLinkEmail(currentEmailAddress);

    resetRequestBeingProcessed();
  }

  function handleSendSigninWithEmailLinkEmailCanceled(): void {
    // call method to reset the local state details for hiding/showing the Sign-in With Email LInk Email dialog
    sendSigninWithEmailLinkEmailConfirmationReset();
  }

  function sendSigninWithEmailLinkEmailConfirmationReset() {
    // set flag to hide the delete confirmation dialog
    setShowSendSigninWithEmailLinkEmailConfirmationDlg(false);
  }

  function handleEmailRequiredDialogButtonClick() {
    setShowEmailRequiredDlg(false);
  }

  function resetRequestBeingProcessed() {
    setRequestBeingProcessed(false);
    setRequestBeingProcessedLabel('');
  }

  displayConsoleLogs && console.log(`%c LoginForm. Before return statement (new). accountNotVerified: ${accountNotVerified}; getValues('email'): ${getValues('email')}; currentEmailAddress: ${currentEmailAddress}; emailAtLastSubmit: ${emailAtLastSubmit}`, 'background: #055; color: #fff');

  return (
    <>
      <Form
        onSubmit={handleSubmit(handleLoginSubmit)}
      >
        {/* if there's an action in progress, display the progress bar area */}
        {requestBeingProcessed &&
          <StyledBoxForProgressBarArea>
            <StyledLinearProgressForProgressBar />
            <StyledBoxForProgressLabelArea>
              {/* if an action progress label has been provided, display it */}
              {requestBeingProcessedLabel &&
                <StyledTypographyForProgressLabel>{requestBeingProcessedLabel}</StyledTypographyForProgressLabel>
              }
            </StyledBoxForProgressLabelArea>
          </StyledBoxForProgressBarArea>
        }

        <TextField
          inputRef={emailReg}
          {...emailProps}
          autoFocus
          label={ControlsStringAssets.emailLabel}
          margin='normal'
          fullWidth
          error={!!errors.email}
          helperText={errors?.email?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onEmailAddressAnimationStart()
            }
          }}
          onChange={e => setCurrentEmailAddress(e.target.value)}
        />

        { /* If we had detected an "Account Not Verified" error -AND- the email address hasn't changed since the last submit... */
          accountNotVerified && (emailAtLastSubmit === getValues('email')) &&
          <Box>
            <StyledSpanForActionText onClick={handleResendVerificationEmail}>
              {ActionTextStringAssets.resendAccountVerificationEmail}
            </StyledSpanForActionText>
          </Box>
        }

        <Box>
          <StyledSpanForActionText onClick={handleSendSigninWithEmailLinkEmail}>
            {ActionTextStringAssets.sendSigninWithEmailLinkEmail}
          </StyledSpanForActionText>
        </Box>

        {/* Confirm Re-sending Account Verification Email Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showResendVerificationEmailConfirmationDlg}
          headerText={MessagesStringAssets.login_ResendVerificationEmailConfirmationHeader}
          bodyText={composeMessageUsingStringAsset(MessagesStringAssets.login_ResendVerificationEmailConfirmation, currentEmailAddress, MessagesStringAssets.substitutionKeyword)}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handleResendVerificationEmailConfirmed}
          onNonAcceptance={handleResendVerificationEmailCanceled}
        />

        {/* Confirm Sending Password Reset Email Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showSendPasswordResetEmailConfirmationDlg}
          headerText={MessagesStringAssets.login_SendPasswordResetEmailConfirmationHeader}
          bodyText={composeMessageUsingStringAsset(MessagesStringAssets.login_SendPasswordResetEmailConfirmation, currentEmailAddress, MessagesStringAssets.substitutionKeyword)}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handlePasswordResetEmailConfirmed}
          onNonAcceptance={handleSendPasswordResetEmailCanceled}
        />

        {/* Confirm Sending Sign-in With Email Link Email Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showSendSigninWithEmailLinkEmailConfirmationDlg}
          headerText={MessagesStringAssets.login_SendSigninWithEmailLinkEmailConfirmationHeader}
          bodyText={composeMessageUsingStringAsset(MessagesStringAssets.login_SendSigninWithEmailLinkEmailConfirmation, currentEmailAddress, MessagesStringAssets.substitutionKeyword)}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handleSendSigninWithEmailLinkEmailConfirmed}
          onNonAcceptance={handleSendSigninWithEmailLinkEmailCanceled}
        />


        {/* Dialog to display message indicating that a valid email address is required */}
        <OneButtonDialog
          showDialog={showEmailRequiredDlg}
          headerText={MessagesStringAssets.login_AValidEmailAddressRequiredHeader}
          bodyText={MessagesStringAssets.login_AValidEmailAddressRequired}
          buttonText={ControlsStringAssets.okButtonText}
          onButtonClick={handleEmailRequiredDialogButtonClick}
        />

        <TextField
          inputRef={passwordReg}
          {...passwordProps}
          type="password"
          label={ControlsStringAssets.passwordLabel}
          margin='normal'
          fullWidth
          error={!!errors.password}
          helperText={errors?.password?.message}
          slotProps={{
            inputLabel: {
              required: true // this will cause an asterisk ('*') to appear at the end of the label text
            },
            input: {
              onAnimationStart: onPasswordAnimationStart()
            }
          }}
          onChange={e => setCurrentPassword(e.target.value)}
        />

        <Box>
          <StyledSpanForActionText onClick={handlePasswordResetEmail}>
            {ActionTextStringAssets.forgotPassword}
          </StyledSpanForActionText>
        </Box>

        {/* if there's a login in progress, display the progress bar area */}
        {loginInProgress &&
          <StyledBoxForProgressBarArea>
            <StyledLinearProgressForProgressBar />
            <StyledBoxForProgressLabelArea>
              {/* if an action progress label has been provided, display it */}
              <Typography variant="caption">{MessagesStringAssets.login_Requested}</Typography>
            </StyledBoxForProgressLabelArea>
          </StyledBoxForProgressBarArea>
        }
        {/* display the action buttons ("Login" & "Create New Account") */}

        <StyledBoxForActionButtonsArea>
          <SubmitButton
            fullWidth
            disabled={!formIsValid || loginInProgress}
          >
            {ControlsStringAssets.loginButtonText}
          </SubmitButton>

          <StyledBoxForPseudoHorizontalRule>
            <StyledBoxForTextInPseudoHorizontalRule>OR</StyledBoxForTextInPseudoHorizontalRule>
          </StyledBoxForPseudoHorizontalRule>

          <StyledButtonForRegisterButton
            disabled={loginInProgress}
            onClick={() => navigate('/createNewAccount')}
          >
            {ControlsStringAssets.createNewAccountButtonText}
          </StyledButtonForRegisterButton>
        </StyledBoxForActionButtonsArea>

      </Form>
    </>

  );
}

export default LoginForm;